import { ConnectedPosition } from '@angular/cdk/overlay';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import { User } from 'src/app/services/auth/interfaces';
import {
  Message,
  MessageListResponse,
} from 'src/app/services/messages/interfaces';
import { MessagesService } from 'src/app/services/messages/messages.service';

interface NotificationGroup {
  label: string;
  notifications: Message[];
}

@Component({
  selector: 'app-messages',
  templateUrl: './messages.component.html',
  styleUrls: ['./messages.component.scss'],
})
export class MessagesComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  notificationsOpen = false;
  showUnreadOnly = false;

  user: User | null = null;
  messages: Message[] = [];

  currentPage = 0;
  pageSize = 5;
  totalPages = 0;
  totalElements = 0;
  isLoadingMore = false;
  hasMoreData = true;

  private _cachedGroupedNotifications: NotificationGroup[] = [];
  private _lastFilteredLength = 0;
  private _lastShowUnreadOnly = false;

  overlayPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 8,
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: -8,
    },
  ];

  constructor(
    private messagesService: MessagesService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.setCurrentUser();
    this.getMessagesList();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setCurrentUser(): void {
    this.authService.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.user = user;
    });
  }

  getMessagesList(append: boolean = false): void {
    if (!this.user?.tenant?.id) {
      return;
    }

    if (append && this.isLoadingMore) {
      return;
    }

    if (!this.hasMoreData && append) {
      return;
    }

    if (append) {
      this.isLoadingMore = true;
    }

    this.messagesService
      .getMessagesByTenantID({
        tenantId: this.user.tenant.id,
        page: this.currentPage,
        size: this.pageSize,
        viewed: false,
      })
      .pipe(
        take(1),
        finalize(() => {
          this.isLoadingMore = false;
        })
      )
      .subscribe({
        next: (response: MessageListResponse) => {
          if (append) {
            this.messages = [...this.messages, ...response.content];
          } else {
            this.messages = response.content;
          }

          this.totalElements = response.totalElements || 0;
          this.totalPages = response.totalPages || 0;

          this.hasMoreData = !response.last;

          if (this.hasMoreData) {
            this.currentPage++;
          }

          this.clearGroupCache();
        },
        error: () => {
          this.messages = [];
          this.hasMoreData = false;
          this.clearGroupCache();
        },
      });
  }

  onScrollDown(): void {
    if (!this.isLoadingMore && this.hasMoreData) {
      this.getMessagesList(true);
    }
  }

  resetPagination(): void {
    this.messages = [];
    this.currentPage = 0;
    this.hasMoreData = true;
    this.totalElements = 0;
    this.totalPages = 0;
    this.isLoadingMore = false;
    this.clearGroupCache();
  }

  toggleNotifications(): void {
    this.notificationsOpen = !this.notificationsOpen;
  }

  toggleUnreadFilter(): void {
    this.showUnreadOnly = !this.showUnreadOnly;
    this.clearGroupCache();
  }

  refreshMessages(forceRefresh: boolean = true): void {
    this.resetPagination();
    this.getMessagesList();
  }

  get filteredMessages(): Message[] {
    if (this.showUnreadOnly) {
      return this.messages.filter((m) => !m.viewed);
    }
    return this.messages;
  }

  get groupedNotifications(): NotificationGroup[] {
    const filtered = this.filteredMessages;

    if (
      filtered.length === this._lastFilteredLength &&
      this.showUnreadOnly === this._lastShowUnreadOnly &&
      this._cachedGroupedNotifications.length > 0
    ) {
      return this._cachedGroupedNotifications;
    }

    this._lastFilteredLength = filtered.length;
    this._lastShowUnreadOnly = this.showUnreadOnly;

    const now = new Date();

    const today: Message[] = [];
    const yesterday: Message[] = [];
    const thisWeek: Message[] = [];
    const older: Message[] = [];

    filtered.forEach((notification) => {
      const notificationDate = new Date(notification.createdAt);
      const diffInMs = now.getTime() - notificationDate.getTime();
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

      if (diffInDays === 0) {
        today.push(notification);
      } else if (diffInDays === 1) {
        yesterday.push(notification);
      } else if (diffInDays <= 7) {
        thisWeek.push(notification);
      } else {
        older.push(notification);
      }
    });

    const groups: NotificationGroup[] = [];

    if (today.length > 0) {
      groups.push({ label: 'Hoje', notifications: today });
    }
    if (yesterday.length > 0) {
      groups.push({ label: 'Ontem', notifications: yesterday });
    }
    if (thisWeek.length > 0) {
      groups.push({ label: 'Esta semana', notifications: thisWeek });
    }
    if (older.length > 0) {
      groups.push({ label: 'Mais antigas', notifications: older });
    }

    this._cachedGroupedNotifications = groups;
    return groups;
  }

  get unreadCount(): number {
    return this.messages.filter((m) => !m.viewed).length;
  }

  markAllAsRead(): void {
    const unreadMessages = this.messages.filter((m) => !m.viewed);

    if (unreadMessages.length === 0 || !this.user?.tenant?.id) {
      return;
    }

    unreadMessages.forEach((m) => (m.viewed = true));
    this.clearGroupCache();

    this.messagesService
      .markAllMessagesAsRead(this.messages, this.user.tenant.id)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.updateCacheWithCurrentMessages();
        },
        error: () => {
          unreadMessages.forEach((m) => (m.viewed = false));
          this.clearGroupCache();
        },
      });
  }

  openNotification(message: Message): void {
    if (!message.viewed) {
      message.viewed = true;
      this.clearGroupCache();

      if (this.user?.tenant?.id) {
        this.messagesService
          .markMessageAsReadByID(message.id, this.user.tenant.id)
          .pipe(take(1), takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.updateCacheWithCurrentMessages();
            },
            error: () => {
              message.viewed = false;
              this.clearGroupCache();
            },
          });
      }
    }
  }

  private clearGroupCache(): void {
    this._cachedGroupedNotifications = [];
  }

  private updateCacheWithCurrentMessages(): void {
    if (!this.user?.tenant?.id) {
      return;
    }

    for (
      let page = 0;
      page <= Math.floor(this.messages.length / this.pageSize);
      page++
    ) {
      const cacheKey = `${this.user.tenant.id}-${page}-${this.pageSize}`;
      const startIndex = page * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      const pageMessages = this.messages.slice(startIndex, endIndex);

      if (pageMessages.length > 0) {
        const response: MessageListResponse = {
          content: pageMessages,
          totalElements: this.totalElements,
          totalPages: this.totalPages,
          last: page >= this.totalPages - 1,
          size: this.pageSize,
          number: page,
          first: page === 0,
          empty: pageMessages.length === 0,
          numberOfElements: pageMessages.length,
        } as MessageListResponse;

        this.messagesService.updateCacheEntry(cacheKey, response);
      }
    }
  }

  trackByGroup(index: number, group: NotificationGroup): string {
    return group.label;
  }

  trackByNotification(index: number, notification: Message): string {
    return notification.id.toString();
  }

  getNotificationIcon(type: string): string {
    const iconMap: { [key: string]: string } = {
      EXPIRED_DOCUMENT: 'description',
      SOON_TO_EXPIRE_DOCUMENT: 'schedule',
      REJECTED_DOCUMENT: 'cancel',
    };

    return iconMap[type] || 'notifications';
  }

  getNotificationTypeLabel(type: string): string {
    const labelMap: { [key: string]: string } = {
      EXPIRED_DOCUMENT: 'Documento Expirado',
      SOON_TO_EXPIRE_DOCUMENT: 'Documento Expirando',
      REJECTED_DOCUMENT: 'Documento Rejeitado',
    };

    return labelMap[type] || 'Notificação';
  }

  getRelativeTime(date: Date): string {
    const dateObj = new Date(date);
    const now = new Date();
    const diffInMs = now.getTime() - dateObj.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) {
      return 'Agora mesmo';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} min atrás`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h atrás`;
    } else if (diffInDays === 1) {
      return 'Ontem';
    } else if (diffInDays < 7) {
      return `${diffInDays} dias atrás`;
    } else {
      return dateObj.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'short',
      });
    }
  }
}
